/**
 * FriendsPage — single-page CRUD for friend graph. Lists accepted
 * friends + incoming requests + outgoing requests, with a search box to
 * send a new friend request by handle.
 */

import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { apiPath } from '../api.js';

type Friend = {
  user_id: string;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  status: 'pending' | 'accepted' | 'blocked';
  direction: 'incoming' | 'outgoing' | 'mutual';
  last_seen_at: number | null;
};

const ONLINE_S = 5 * 60;

export function FriendsPage(): ReactElement {
  const [friends, setFriends] = useState<readonly Friend[]>([]);
  const [searchHandle, setSearchHandle] = useState('');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await fetch(apiPath('/api/friends'), { credentials: 'include' });
    if (!r.ok) {
      setMessage(`fetch failed: ${r.status}`);
      return;
    }
    const json = (await r.json()) as { ok: true; friends: Friend[] };
    setFriends(json.friends);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sendRequest = useCallback(async () => {
    const handle = searchHandle.trim();
    if (!handle) return;
    setPending(true);
    setMessage(null);
    try {
      const r = await fetch(apiPath('/api/friends/request'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ handle }),
      });
      const j = (await r.json()) as { ok?: boolean; error?: string };
      if (!r.ok || !j.ok) {
        setMessage(j.error ?? `error ${r.status}`);
      } else {
        setMessage(`request sent to ${handle}`);
        setSearchHandle('');
        await refresh();
      }
    } finally {
      setPending(false);
    }
  }, [searchHandle, refresh]);

  const accept = useCallback(
    async (handle: string) => {
      await fetch(apiPath('/api/friends/accept'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ handle }),
      });
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (handle: string) => {
      await fetch(apiPath(`/api/friends/${encodeURIComponent(handle)}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      await refresh();
    },
    [refresh],
  );

  const accepted = friends.filter((f) => f.status === 'accepted');
  const incoming = friends.filter((f) => f.status === 'pending' && f.direction === 'incoming');
  const outgoing = friends.filter((f) => f.status === 'pending' && f.direction === 'outgoing');

  return (
    <main className="mx-auto max-w-3xl space-y-6 px-4 py-6 text-cosmos-100">
      <header className="border-cosmos-800 border-b pb-4">
        <h1 className="text-2xl font-semibold">Friends</h1>
      </header>

      <section className="rounded border border-cosmos-700 bg-cosmos-900/50 p-4">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-cosmos-400">
          Add a friend by handle
        </h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchHandle}
            onChange={(e) => setSearchHandle(e.target.value)}
            placeholder="handle"
            className="flex-1 rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={sendRequest}
            disabled={pending}
            className="rounded bg-amber-600 px-3 py-1 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-40"
          >
            Send request
          </button>
        </div>
        {message ? <p className="mt-2 text-xs text-cosmos-300">{message}</p> : null}
      </section>

      <FriendList title="Friends" friends={accepted} onRemove={remove} />
      <FriendList
        title="Incoming requests"
        friends={incoming}
        onAccept={accept}
        onRemove={remove}
      />
      <FriendList
        title="Sent requests"
        friends={outgoing}
        onRemove={remove}
        emptyHint="No outstanding requests."
      />
    </main>
  );
}

function FriendList({
  title,
  friends,
  onAccept,
  onRemove,
  emptyHint,
}: {
  title: string;
  friends: readonly Friend[];
  onAccept?: (handle: string) => void;
  onRemove: (handle: string) => void;
  emptyHint?: string;
}): ReactElement {
  if (friends.length === 0 && !emptyHint) return <></>;
  const now = Math.floor(Date.now() / 1000);
  return (
    <section>
      <h2 className="mb-1 text-xs font-medium uppercase tracking-wide text-cosmos-400">{title}</h2>
      {friends.length === 0 ? (
        <p className="text-xs text-cosmos-500 italic">{emptyHint}</p>
      ) : (
        <ul className="divide-y divide-cosmos-800 rounded border border-cosmos-800">
          {friends.map((f) => {
            const online = f.last_seen_at !== null && now - f.last_seen_at < ONLINE_S;
            return (
              <li key={f.user_id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span
                  className={`h-2 w-2 rounded-full ${online ? 'bg-emerald-400' : 'bg-cosmos-700'}`}
                />
                <a
                  href={`/u/${encodeURIComponent(f.handle)}`}
                  className="font-medium hover:underline"
                >
                  {f.display_name ?? f.handle}
                </a>
                <span className="font-mono text-xs text-cosmos-400">@{f.handle}</span>
                <div className="ml-auto flex gap-1">
                  {onAccept ? (
                    <button
                      type="button"
                      onClick={() => onAccept(f.handle)}
                      className="rounded bg-emerald-600 px-2 py-0.5 text-xs text-white hover:bg-emerald-500"
                    >
                      Accept
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onRemove(f.handle)}
                    className="rounded border border-cosmos-700 px-2 py-0.5 text-xs hover:bg-cosmos-800"
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
